import { useEffect, useState } from 'react'
import {
  fetchUserFinancialAccounts,
  createUserManualFinancialAccount,
} from '@/actions/admin/adminFinancialAccountActions'
import { fetchUserPlaidItems } from '@/actions/admin/adminPlaidItemActions'
import AccountCard from './AccountCard'
import { User } from '../../../reducers/auth/userReducer'
import { filterNulls, useAppDispatch } from '@/utils/typeHelpers'
import { useReselector } from '@/utils/sharedHooks'
import {
  getManualFinancialAccountsByUserId,
  getPlaidOnlyFinancialAccountsByUserId,
  selectAdminPlaidItemsByUserId,
} from '@/selectors/financeSelectors'
import { AdminInstitutionCard } from './AdminInstitutionCard'
import { Form } from 'semantic-ui-react'
import { Dropdown, Modal } from '@/componentsV1/BaseComponents'

import {
  Card,
  Text,
  Button,
  CardColors,
  Loader,
  Icon,
  Tooltip,
  Alert,
} from '@/ui'
import {
  faCheckCircle,
  faQuestionCircle,
} from '@fortawesome/pro-solid-svg-icons'
import { TypographyStyles } from '@/styles'

const ACCOUNT_TYPE_OPTIONS = [
  { text: 'depository', value: 'depository' },
  { text: 'credit', value: 'credit' },
  { text: 'loan', value: 'loan' },
]

const ACCOUNT_SUBTYPE_OPTIONS = [
  { text: 'checking', value: 'checking' },
  { text: 'savings', value: 'savings' },
  { text: 'credit card', value: 'credit card' },
  { text: 'line of credit', value: 'line of credit' },
  { text: 'auto', value: 'auto' },
]
const FinancialAccountCreateModal = ({
  user,
  open,
  close,
}: {
  user: User
  open: boolean
  close: () => void
}) => {
  const dispatch = useAppDispatch()

  const [plaidInstitutionName, setPlaidInstitutionName] = useState('')
  const [name, setName] = useState('')
  const [mask, setMask] = useState('')
  const [type, setType] = useState('')
  const [subtype, setSubtype] = useState('')
  const [created, setCreated] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [plaidAccountId, setPlaidAccountId] = useState<number>()

  const plaidAccounts = useReselector(
    getPlaidOnlyFinancialAccountsByUserId,
    user.id
  )

  const plaidAccountOptions = plaidAccounts.map((account) => ({
    text: `${account.plaidInstitutionName} ${account.name}: ${account.mask}`,
    value: account.id,
  }))

  const handleCreateAccount = async () => {
    if (!user.financialProfile?.id) {
      return
    }

    setSubmitting(true)
    const res = await createUserManualFinancialAccount(user.id, {
      financialProfileId: user.financialProfile?.id,
      plaidInstitutionName,
      name,
      mask,
      type,
      subtype,
      plaidAccountId,
    })(dispatch)
    setSubmitting(false)
    if (res) {
      setCreated(true)
    }
  }

  useEffect(() => {
    if (plaidAccountId) {
      const plaidAccount = plaidAccounts.find(
        (account) => account.id === plaidAccountId
      )
      setPlaidInstitutionName(plaidAccount?.plaidInstitutionName || '')
      setName(`Manual ${plaidAccount?.name || ''}`)
      setMask(plaidAccount?.mask || '')
      setType(plaidAccount?.type || '')
      setSubtype(plaidAccount?.subtype || '')
    }
  }, [plaidAccountId, plaidAccounts])

  const resetForm = () => {
    setPlaidInstitutionName('')
    setName('')
    setMask('')
    setType('')
    setSubtype('')
    setCreated(false)
    setSubmitting(false)
    setPlaidAccountId(undefined)
  }

  useEffect(() => {
    if (open) resetForm()
  }, [open])

  const isSubmitDisabled =
    submitting || !subtype || !type || !mask || !name || !plaidInstitutionName

  const onClose = () => {
    resetForm()
    close()
  }

  const renderContent = () => {
    if (created) {
      return (
        <Modal.Content>
          <Icon icon={faCheckCircle} color="green" size="lg" />
          <p>
            Account successfully created! You can now upload transactions
            manually.
          </p>
        </Modal.Content>
      )
    } else {
      return (
        <Modal.Content>
          <Alert variant="warning">
            <b>
              Warning: You are creating a manual financial account for this
              user.
            </b>
            <br />
            This means that the user is unable to connect this account via
            Plaid, and this account now requires you to manually upload
            transactions.
            <br />
            <b>Please only use as a last resort.</b>
          </Alert>
          <Form className="mt-6">
            <Form.Group>
              <Form.Field width={16}>
                <Dropdown
                  fluid
                  clearable
                  label="Plaid Connected Account"
                  placeholder="Select Account"
                  options={plaidAccountOptions}
                  onChange={setPlaidAccountId}
                  value={plaidAccountId}
                />
              </Form.Field>
            </Form.Group>
            <Form.Group widths="equal" className="mt-4">
              <Form.Input
                required
                fluid
                label="Institution Name"
                placeholder="Institution Name"
                onChange={(name, target) =>
                  setPlaidInstitutionName(target.value)
                }
                name="plaidInstitutionName"
                value={plaidInstitutionName}
              />
              <Form.Input
                required
                fluid
                label="Account Name"
                placeholder="Account Name"
                onChange={(name, target) => setName(target.value)}
                name="name"
                value={name}
              />
            </Form.Group>
            <div className="flex items-end gap-4 mt-4">
              <div className="w-1/4">
                <Form.Input
                  required
                  fluid
                  label="Last 4 of Account"
                  placeholder="****"
                  maxLength={4}
                  onChange={(name, target) => setMask(target.value)}
                  name="mask"
                  value={mask}
                />
              </div>
              <div className="flex-1">
                <Dropdown
                  required
                  fluid
                  label="Account Type"
                  placeholder="Select Account Type"
                  options={ACCOUNT_TYPE_OPTIONS}
                  onChange={setType}
                  value={type}
                />
              </div>
              <div className="flex-1">
                <Dropdown
                  required
                  fluid
                  label="Account Subtype"
                  placeholder="Select Subtype"
                  options={ACCOUNT_SUBTYPE_OPTIONS}
                  onChange={setSubtype}
                  value={subtype}
                />
              </div>
            </div>
          </Form>
        </Modal.Content>
      )
    }
  }

  return (
    <Modal
      size={'small'}
      dimmer={'inverted'}
      className="financialAccountCreateModal"
      open={open}
      onClose={onClose}
    >
      <Modal.Header>
        <Text variant={TypographyStyles.H3}>
          Create a Manual Financial Account for {user.firstName} {user.lastName}
        </Text>
      </Modal.Header>
      {renderContent()}
      <Modal.Actions className="flex gap-3 justify-end">
        <Button onClick={onClose}>{created ? 'Close' : 'Cancel'}</Button>
        {!created && (
          <Button
            variant="primary"
            disabled={isSubmitDisabled}
            onClick={handleCreateAccount}
          >
            {submitting ? 'Creating account...' : 'Create Account'}
          </Button>
        )}
      </Modal.Actions>
    </Modal>
  )
}

const FinancialAccountsCard = ({ user }: { user: User }) => {
  const dispatch = useAppDispatch()
  const [loading, setLoading] = useState(true)
  const [modalOpen, setModalOpen] = useState(false)

  const manualAccounts = useReselector(
    getManualFinancialAccountsByUserId,
    user.id
  )
  const adminPlaidItemsByUserId = useReselector(
    selectAdminPlaidItemsByUserId,
    user.id
  )

  useEffect(() => {
    const fetch = async () => {
      await dispatch(fetchUserFinancialAccounts(user.id))
      await dispatch(fetchUserPlaidItems(user.id))
      setLoading(false)
    }
    fetch()
  }, [dispatch, user.id])

  return (
    <Card color={CardColors.LEVEL_0} className="w-full">
      <div className="flex items-center justify-between mb-4">
        <Text variant={TypographyStyles.H2}>Financial Institutions</Text>
        <div className="flex items-center space-x-2">
          <Button variant="link" onClick={() => setModalOpen(true)}>
            Add Manual Account
          </Button>
          <Tooltip>
            <Tooltip.Trigger asChild>
              <Icon icon={faQuestionCircle} />
            </Tooltip.Trigger>
            <Tooltip.Content>
              <Text>Use for accounts that cannot be connected via Plaid</Text>
            </Tooltip.Content>
          </Tooltip>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 items-start">
        {adminPlaidItemsByUserId &&
          !loading &&
          filterNulls(adminPlaidItemsByUserId).map((institution) => (
            <div key={institution.id} className="self-start h-full">
              <AdminInstitutionCard plaidItem={institution} userId={user.id} />
            </div>
          ))}
        {manualAccounts &&
          !loading &&
          filterNulls(manualAccounts).map((account) => (
            <div key={account.id} className="self-start h-full">
              <AccountCard
                key={account.id}
                account={account}
                isAdmin
                userId={user.id}
              />
            </div>
          ))}
      </div>
      {loading && <Loader className="mt-4" />}
      <FinancialAccountCreateModal
        close={() => setModalOpen(false)}
        open={modalOpen}
        user={user}
      />
    </Card>
  )
}

export default FinancialAccountsCard
